import React, { useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';

const BankVerificationDashboard = ({ extractedData, onVerify }) => {
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedAccount, setVerifiedAccount] = useState('');

  const handleVerify = async () => {
    const currentAccount = extractedData?.account_number;
    if (isVerifying || !currentAccount || verifiedAccount === currentAccount) return;
    
    setIsVerifying(true);
    try {
      const result = await onVerify(extractedData);
      setVerificationResult(result);
      setVerifiedAccount(currentAccount);
    } catch (error) {
      setVerificationResult({
        status: 'error',
        message: 'Verification failed',
        verified: false
      });
    } finally {
      setIsVerifying(false);
    }
  };

  if (!extractedData?.account_number || !extractedData?.ifsc_code) {
    return null;
  }

  if (extractedData.account_number !== verifiedAccount && verificationResult) {
    setVerificationResult(null);
    setVerifiedAccount('');
  }

  const isAlreadyVerified = verifiedAccount === extractedData.account_number && verificationResult;

  return (
    <div className="w-full p-4 border-2 rounded-lg bg-blue-50 border-blue-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-900">Bank Account Verification</h3>
        <button
          onClick={handleVerify}
          disabled={isVerifying || isAlreadyVerified}
          className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
        >
          {isVerifying ? 'Verifying...' : isAlreadyVerified ? '✓ Verified' : 'Verify with Surepass API'}
        </button>
      </div>

      {verificationResult && (
        <div className="space-y-3">
          {verificationResult.status === 'error' ? (
            <div className="p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2">
              <FiX className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Verification Failed</p>
                <p className="text-xs text-red-600 mt-1">{verificationResult.message}</p>
              </div>
            </div>
          ) : verificationResult.verified ? (
            <>
              <div className="p-3 bg-green-50 border border-green-300 rounded-lg flex items-start gap-2">
                <FiCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">Bank Account Verified Successfully</p>
                  <p className="text-xs text-green-600 mt-1">Account exists and is active</p>
                </div>
              </div>

              {verificationResult.data && (
                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2">Verified Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Account Holder:</span>
                      <span className="font-medium text-blue-900">{verificationResult.data.full_name}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Account Status:</span>
                      <span className="font-medium text-green-600">
                        {verificationResult.data.account_exists ? 'Active' : 'Not Found'}
                      </span>
                    </div>
                    {verificationResult.data.upi_id && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">UPI ID:</span>
                        <span className="font-medium text-blue-900">{verificationResult.data.upi_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {verificationResult.data?.ifsc_details && (
                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2">Bank Branch Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-600">Bank:</span>
                      <div className="font-medium text-blue-900">{verificationResult.data.ifsc_details.bank_name}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Branch:</span>
                      <div className="font-medium text-blue-900">{verificationResult.data.ifsc_details.branch}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">City:</span>
                      <div className="font-medium text-blue-900">{verificationResult.data.ifsc_details.city}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">State:</span>
                      <div className="font-medium text-blue-900">{verificationResult.data.ifsc_details.state}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Address:</span>
                      <div className="font-medium text-blue-900">{verificationResult.data.ifsc_details.address}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Supported Services:</span>
                      <div className="flex gap-2 mt-1">
                        {verificationResult.data.ifsc_details.imps && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">IMPS</span>}
                        {verificationResult.data.ifsc_details.rtgs && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">RTGS</span>}
                        {verificationResult.data.ifsc_details.neft && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">NEFT</span>}
                        {verificationResult.data.ifsc_details.upi && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">UPI</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-3 bg-red-50 border border-red-300 rounded-lg">
              <p className="text-sm font-medium text-red-800">Account Not Found</p>
              <p className="text-xs text-red-600 mt-1">The provided account details could not be verified</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankVerificationDashboard;
